// /visa/$nationality — hub for one nationality's visa options
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { VISA_NATIONALITIES, getVisaNationality, getVisaType } from "@/data/visa-nationalities";
import { VISA_TYPES } from "@/data/visa-types";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, howToSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { TranslateVisaCrossLink } from "@/components/TranslateVisaCrossLink";
import VisaNationalityTripleKeyLinks from "@/components/VisaNationalityTripleKeyLinks";
import { Plane, ArrowRight, ShieldCheck, MessageCircle, Phone, BadgeCheck } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/visa/$nationality/")({
  loader: ({ params }) => {
    const n = getVisaNationality(params.nationality);
    if (!n) throw notFound();
    return { slug: params.nationality };
  },
  head: ({ params }) => {
    const n = getVisaNationality(params.nationality);
    if (!n) return { meta: [{ title: ctaTitle("ไม่พบสัญชาติ", "/visa/$nationality", "th")}] };
    const url = `${SITE}/visa/${n.slug}`;
    const title = `วีซ่าไทยสำหรับคน${n.nameTh} ${n.flag} · ครบทุกประเภท Non-B/LTR/DTV/Elite | NYC`;
    const desc = `บริการวีซ่าไทยสำหรับชาว${n.nameTh} (${n.countryEn}) — Non-B, LTR, DTV, Elite, Retirement, Marriage, ED ${n.visaExempt > 0 ? `Visa Exempt ${n.visaExempt} วัน` : "ต้องขอล่วงหน้า"}`;
    return {
      meta: [
        { title: ctaTitle(title, "/visa/$nationality", "th") }, { name: "description", content: ctaDesc(desc, "/visa/$nationality", "th")},
        { name: "keywords", content: n.keywords.join(", ") },
        { property: "og:title", content: ctaTitle(title, "/visa/$nationality", "th")}, { property: "og:description", content: ctaDesc(desc, "/visa/$nationality", "th")},
        { property: "og:url", content: url }, { property: "og:locale", content: "th_TH" },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks(`/visa/${n.slug}`, `/en/visa/${n.slug}`)],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "CollectionPage", "@id": `${url}#page`,
              name: title, description: desc,
              hasPart: n.topVisaTypes.map((vt) => {
                const v = getVisaType(vt);
                return v ? { "@type": "WebPage", name: `${v.nameTh} สำหรับคน${n.nameTh}`, url: `${SITE}/visa/${n.slug}/${vt}` } : null;
              }).filter(Boolean),
            }),
          },
          howToSchema({
            name: `วิธีขอวีซ่าไทยสำหรับคน${n.nameTh}`,
            description: `ขั้นตอนเตรียมเอกสาร แปล รับรอง และยื่นวีซ่าไทยสำหรับชาว${n.nameTh}`,
            totalTime: "P10D",
            estimatedCost: { currency: "THB", value: "8000" },
            inLanguage: "th",
            steps: [
              { name: "เลือกประเภทวีซ่า", text: `เลือกจาก ${n.topVisaTypes.length}+ วีซ่าที่เหมาะกับคน${n.nameTh} (Non-B/LTR/DTV/Elite/Retirement/Marriage)`, url: `${SITE}/visa/${n.slug}` },
              { name: "เตรียมเอกสารต้นทาง", text: `รวบรวมเอกสารจากประเทศ${n.countryEn} — Police Clearance, Bank Statement, ใบรับรองงาน`, url: `${SITE}/checklist` },
              { name: "แปลรับรอง", text: "แปลเอกสารโดยนักแปลขึ้นทะเบียน + รับรองคำแปล", url: `${SITE}/translate` },
              { name: "MFA + Apostille", text: "ยื่นกระทรวงต่างประเทศไทย + Apostille หากต้องใช้ที่ต่างประเทศ", url: `${SITE}/apostille` },
              { name: "ยื่นสถานทูต / ตม.", text: "ยื่นแทนที่สถานทูตไทย หรือสำนักงานตรวจคนเข้าเมือง (Chaengwattana)", url: `${SITE}/visa` },
            ],
          }),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList", "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "วีซ่า", item: `${SITE}/visa` },
                { "@type": "ListItem", position: 3, name: `วีซ่าสำหรับคน${n.nameTh}`, item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  component: VisaNationalityHub,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">ไม่พบสัญชาติ</h1>
      <Link to="/visa" className="mt-4 inline-block text-gold underline">กลับไปยังรายการทั้งหมด</Link>
    </main>
  ),
});

function VisaNationalityHub() {
  const { slug } = Route.useLoaderData() as { slug: string };
  const n = getVisaNationality(slug)!;
  const types = n.topVisaTypes.map((id) => VISA_TYPES.find((v) => v.id === id)).filter(Boolean) as typeof VISA_TYPES;
  const others = VISA_NATIONALITIES.filter((x) => x.slug !== slug && x.region === n.region).slice(0, 6);

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_60%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/visa" className="hover:text-gold">วีซ่า</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{n.nameTh}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Plane className="h-3.5 w-3.5" /> Thai Visa · {n.countryEn}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            <span className="mr-3 text-4xl">{n.flag}</span>
            วีซ่าไทยสำหรับคน{n.nameTh}
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">{n.notes}</p>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md transition hover:-translate-y-0.5 hover:bg-emerald-400">
              <MessageCircle className="h-4 w-4" /> ปรึกษาผ่าน LINE
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md transition hover:-translate-y-0.5">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable
          </span>
          NYC Legal & Notary Service ให้บริการวีซ่าไทยสำหรับชาว{n.nameTh} ครบทุกประเภท — {types.length} วีซ่า รวม Non-B, LTR, DTV, Elite, Retirement, Marriage, ED — ยื่นแทน + พาไปสถานทูต/ตม. บริการสำเร็จ 2,147+ เคส
        </blockquote>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">วีซ่าที่ใช้ได้สำหรับคน{n.nameTh}</h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {types.map((v) => (
              <Link key={v.id} to="/visa/$nationality/$visatype" params={{ nationality: n.slug, visatype: v.id }}
                className="group flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md">
                <div className="flex-1">
                  <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">{v.code}</div>
                  <div className="mt-0.5 font-display text-base font-extrabold text-navy-deep group-hover:text-amber-600">{v.nameTh}</div>
                  <div className="mt-1 text-[12px] text-slate-600">{v.tagline}</div>
                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-500">
                    <span><ShieldCheck className="mr-1 inline h-3 w-3 text-emerald-600" />{v.durationInitial}</span>
                    <span>{v.serviceFee}</span>
                  </div>
                </div>
                <ArrowRight className="mt-1 h-4 w-4 text-slate-400 group-hover:text-amber-600" />
              </Link>
            ))}
          </div>
        </section>

        {others.length > 0 && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">สัญชาติใกล้เคียง</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {others.map((o) => (
                <Link key={o.slug} to="/visa/$nationality" params={{ nationality: o.slug }}
                  className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md">
                  <div className="text-2xl">{o.flag}</div>
                  <div className="flex-1">
                    <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">{o.nameTh}</div>
                    <div className="mt-0.5 text-[11px] text-slate-500">{o.countryEn}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
        <VisaNationalityTripleKeyLinks
          mode="th"
          nationalityNameTh={`ชาว${n.nameTh}`}
          nationalityDemonymEn={n.demonymEn}
          countryEn={n.countryEn}
        />
      </div>

      <TrustLayer variant="compact" />
      <TranslateVisaCrossLink mode="from-visa" locale="th" />
    </main>
  );
}
